/**
 * 使えるお金が無い（GET /v1/money/prepaid の canSpend が false = プレイヤー側が止まっている）ときの帯（お金以外の画面: プロジェクトの一覧 = 新規作成、プロジェクトの枠 = 概要・デプロイほか）。
 * 判定は prepaidGate.ts、入金そのものはお金の画面（/money/prepaid・Money の持ち物）に任せる = ここはリンクを出すだけ。
 *
 * 取れないとき（読み込み中・古いサーバー・お金の API の一時的な失敗）は何も出さない: 帯は補助で、無くても 402 のエラー側で案内が出る。
 * 30 秒ごとに読み直す: 別タブで入金した後、この画面を開いたままでも帯が消える（止まっていた処理はサーバー側で自動再開）。
 *
 * メール未確認のあいだの「はじめの入金」（2026-09-28・Core SEC-DEVLOGIN-01 で入金も 403 email_unverified になった）:
 *   登録した直後の人は「未確認」かつ「残高なし」。以前の帯は「入金する」を出していたが、押すと入金の画面で email_unverified に
 *   なるだけだった（リーダーの目視で発見）。順番（確認 → 入金 → ゲームの作成）を先に言い、入金のボタンは確認が済むまで出さない
 *
 * 招待クレジットと「少ない」（2026-09-29 creator/launch-close-2・Money の前払いの道の一覧 16）:
 *   - 招待コードを押さえて確認待ち（invitePending）→「確認すると {amount} の招待クレジットが入ります」だけ。入金のボタンは出さない
 *     （確認前は入金も 403・確認すれば入金なしで使い始められる）
 *   - サーバーの balanceStatus が low → 止まる前の黄色の帯（残りがほとんど招待クレジットなら「招待クレジットの残りがわずか」）+ 入金へのリンク。
 *     お金の画面の中は PrepaidPage が同じことをタイルと知らせで出す
 */
import { Link } from 'react-router-dom';
import { useResource, type Resource } from '../hooks';
import { useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { money } from '../money';
import { readInviteStanding } from '../inviteCode';
import { INVITE_CODE_PATH } from './InviteNotAppliedNotice';
import { prepaidBand, type PrepaidLike } from '../prepaidGate';
import { useSession } from '../session';

export type PrepaidResource = Resource<PrepaidLike & { currency?: string }>;

/**
 * hideStart（2026-09-28 creator/home-redesign）: ホーム（ProjectsPage）は「まだ入金していない」（'start'）を帯ではなく流れの 1 段
 *   （homeFlow.ts の topUp）として出すので、帯の方は出さない（二重にしない）。止まっている警告（'owed' / 'empty'）はそのまま出す。
 *   招待の確認待ち（'invitePending'）も同じく隠す: ホームではメールの確認の段が「確認すると {amount} が入ります」を言う（homeFlow.ts）
 * resource: 呼ぶ画面が同じ /v1/money/prepaid を既に読んでいるなら渡す（同じ GET を 2 本走らせない）。無ければ自分で読む
 */
export function PrepaidBand({ hideStart = false, resource }: { hideStart?: boolean; resource?: PrepaidResource } = {}) {
  const own = useResource<PrepaidLike & { currency?: string }>(resource ? null : '/v1/money/prepaid', 30_000);
  const r = resource ?? own;
  const { t } = useT();
  const { developer } = useSession();
  const kind = prepaidBand(r.data);
  if (!kind || !r.data) return null;
  if (hideStart && (kind === 'start' || kind === 'invitePending')) return null;
  const currency = r.data.currency ?? 'usd';
  if (kind === 'invitePending') {
    // readInviteStanding: 壊れた値でも落とさない（読めなければ額を言わない文にはせず、帯ごと出さない = 推測で額を書かない）
    const inv = readInviteStanding(r.data.invite);
    if (!inv) return null;
    return (
      <div className="owed-band owed-band-info" role="status">
        <span>{t('prepaidBand.invitePending', { amount: money(inv.granted, currency) })}</span>
      </div>
    );
  }
  if (kind === 'start' && developer?.emailVerified === false) {
    return (
      <div className="owed-band" role="alert">
        <span>{t('prepaidBand.startUnverified')}</span>
      </div>
    );
  }
  if (kind === 'low' || kind === 'lowInvite') {
    return (
      <div className="owed-band owed-band-warn" role="status">
        <span>{t(`prepaidBand.${kind}`, { balance: money(r.data.balance, currency) })}</span>
        <Link to="/money/prepaid" className="btn btn-sm btn-ghost">
          {t('prepaidBand.link')}
        </Link>
      </div>
    );
  }
  return (
    <div className="owed-band" role="alert">
      <span>{t(kind !== 'start' && isLaunchHidden('payouts') ? `prepaidBand.${kind}Launch` : `prepaidBand.${kind}`, { owed: money(r.data.owed, currency) })}</span>
      <Link to="/money/prepaid" className={`btn btn-sm ${kind === 'start' ? 'btn-primary' : 'btn-danger'}`}>
        {t('prepaidBand.link')}
      </Link>
      {/* まだ入金していない新しい人（'start'）だけ: 招待コードのある人の道（2026-10-29 core/open-signup-build・招待なしで登録した人は $0 から）。
          使い切った人（'empty'）・請求残（'owed'）は招待を 1 回使った後か、入金で戻す話 = 出さない */}
      {kind === 'start' && (
        <Link to={INVITE_CODE_PATH} className="home-step-link">
          {t('invite.haveCode')}
        </Link>
      )}
    </div>
  );
}
